import { AccountInfo, AuthenticationResult, createNestablePublicClientApplication, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { deserializeAuthenticationResult, type SerializedAuthenticationResult } from '@core/shared/msal-dialog-result';
import { environment } from "@env";
import { logger } from "@services/logger/logger.service";
import { ReplaySubject } from "rxjs";

const AUTH_MESSAGE_TYPE = 'r2-copilot-login';

export interface IPreferredMicrosoftAccount
{
    tenantId: string;
    subject: string;
    loginHint?: string;
}

interface IAuthModalParams
{
    message: string;
    origin: string | undefined;
    error?: number;
}

interface IAuthModalMessage
{
    type: typeof AUTH_MESSAGE_TYPE;
    isLoggedIn: boolean;
    error?: string;
}

export abstract class OfficeBase
{
    // MSAL clearCache removes every localStorage key containing "msal".
    // Keep R2's state under keys that cannot be swept up with the token cache.
    public static readonly R2_MSAL_LOGGED_OUT = 'r2_office_logged_out';
    public static readonly R2_MSAL_PREFERRED_ACCOUNT = 'r2_office_preferred_account';
    private static readonly LEGACY_LOGGED_OUT = 'r2_msal_logged_out';
    private static readonly LEGACY_PREFERRED_ACCOUNT = 'r2_msal_preferred_account';
    private static readonly MSAL_SCOPES = [ 'openid', 'email', 'profile' ];
    private static readonly ID_TOKEN_EXPIRY_MARGIN_SECONDS = 30;

    public initState$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    protected hostType!: Office.HostType;
    protected platformType!: Office.PlatformType;
    protected userEmail: string = '';
    protected currentHost!: IOfficeHost;
    protected msalInstance!: IPublicClientApplication;
    protected accountMsalInstance!: IPublicClientApplication;

    constructor()
    {
        this.init();
    }

    public get userHint(): string
    {
        return this.userEmail;
    }

    protected async init(): Promise<void>
    {
        await Office.onReady(async (info: { host: Office.HostType, platform: Office.PlatformType; }) =>
        {
            try
            {
                this.platformType = info.platform;

                if (info.platform && info.host)
                {
                    logger.log(this.constructor.name, 'init', `Office.js is ready. Platform: ${ info.platform }, Host: ${ info.host }`, 'log');

                    this.hostType = info.host;
                    this.platformType = info.platform;

                    await this.updateUserProfile();

                    const isCreated = Boolean(this.currentHost);
                    const host = this.getHost();
                    await host.waitReadyAsync();

                    if (!isCreated)
                    {
                        host.attachHandler(Office.EventType.ItemChanged, this.officeItemChanged.bind(this));
                    }

                    this.initState$.next(true);
                    return;
                }

                this.initState$.next(false);
            }
            catch (e)
            {
                logger.error(this.constructor.name, 'init', 'Error during Office.onReady callback', 'log', e);
                this.initState$.next(false);
            }
        });
    }

    protected async initMsal()
    {
        if (!this.msalInstance)
        {
            const msalConfig = {
                auth: {
                    clientId: environment.msal_client_id,
                    authority: environment.msal_authority,
                    redirectUri: `${ location.origin }${ environment.msal_dialog_path }`,
                    supportsNestedAppAuth: true
                },
                cache: {
                    cacheLocation: "localStorage"
                }
            };
            this.msalInstance = await createNestablePublicClientApplication(msalConfig);
        }
    }

    protected async getLoginHint()
    {
        try
        {
            if (typeof Office !== "undefined" && Office.context)
            {
                const authContext = await (Office.auth as any).getAuthContext();
                if (authContext?.loginHint) return authContext.loginHint;
            }
        } catch (error)
        {
            console.warn("Could not get login hint:", error);
        }
        return null;
    }

    protected async initAccountMsal(): Promise<void>
    {
        if (!this.accountMsalInstance)
        {
            const instance = new PublicClientApplication({
                auth: {
                    clientId: environment.msal_client_id,
                    authority: environment.msal_authority,
                    redirectUri: `${ location.origin }${ environment.msal_dialog_path }`,
                },
                cache: { cacheLocation: 'localStorage' },
            });
            await instance.initialize();
            this.accountMsalInstance = instance;
        }
    }

    public async acquireIdTokenSilent(loginHint?: string | null): Promise<string | null>
    {
        if (this.isLoggedOutFlagSet())
        {
            return null;
        }

        try
        {
            const preferred = this.getPreferredAccount();
            if (preferred || !this.isWebHost())
            {
                await this.initAccountMsal();
                const accounts = preferred ? [] : this.accountMsalInstance.getAllAccounts();
                const account = preferred ? this.accountMsalInstance.getAccount({
                    tenantId: preferred.tenantId, localAccountId: preferred.subject,
                }) : accounts.length === 1 ? accounts[ 0 ] : null;
                if (!account)
                {
                    return null;
                }
                const result = await this.acquireFreshIdTokenSilent(this.accountMsalInstance, account);
                if (!result || result.account?.tenantId !== (preferred?.tenantId ?? account.tenantId)
                    || result.account.localAccountId !== (preferred?.subject ?? account.localAccountId))
                {
                    return null;
                }
                return result.idToken || null;
            }

            await this.initMsal();
            const account = loginHint ? null : this.msalInstance.getActiveAccount() ?? this.msalInstance.getAllAccounts()[ 0 ];
            loginHint = loginHint || this.userHint;
            const result = account
                ? await this.acquireFreshIdTokenSilent(this.msalInstance, account)
                : await this.msalInstance.ssoSilent({
                    scopes: OfficeBase.MSAL_SCOPES, ...(loginHint ? { loginHint } : {}),
                });
            return result?.idToken ?? null;
        }
        catch (error)
        {
            // Any silent failure (including InteractionRequired) returns null; the
            // caller escalates to the interactive popup -> dialog tiers.
            logger.log(this.constructor.name, 'acquireIdTokenSilent', 'silent failed', 'log', error);
            return null;
        }
    }

    private async acquireFreshIdTokenSilent(instance: IPublicClientApplication, account: AccountInfo): Promise<AuthenticationResult | null>
    {
        const request = { scopes: OfficeBase.MSAL_SCOPES, account };
        let result = await instance.acquireTokenSilent(request);
        const expiresSoon = (token: AuthenticationResult): boolean =>
        {
            const expiresAt = (token.idTokenClaims as { exp?: unknown })?.exp;
            return typeof expiresAt === 'number' && Number.isFinite(expiresAt)
                && expiresAt <= Date.now() / 1_000 + OfficeBase.ID_TOKEN_EXPIRY_MARGIN_SECONDS;
        };
        if (expiresSoon(result))
        {
            result = await instance.acquireTokenSilent({ ...request, forceRefresh: true });
        }
        return expiresSoon(result) ? null : result;
    }

    public async acquireIdTokenInteractive(useAccountPicker: boolean, loginHint?: string | null): Promise<string>
    {
        const preferred = useAccountPicker ? null : this.getPreferredAccount();
        if (preferred)
        {
            loginHint = preferred.loginHint;
        }
        // Desktop Office clients (Windows and Mac) have an unreliable/broken NAA
        // broker: the native popup can silently fail or return the host identity.
        // Use the non-brokered dialog for desktop and explicit account selection.
        // Office on the web runs as a plain browser tab and works fine with the
        // native popup below.
        if (!this.isWebHost() || useAccountPicker || preferred)
        {
            return this.acquireIdTokenViaDialog(useAccountPicker || Boolean(preferred && !loginHint), loginHint);
        }

        await this.initMsal();

        const request: Record<string, unknown> = { scopes: OfficeBase.MSAL_SCOPES };
        if (loginHint)
        {
            request[ 'loginHint' ] = loginHint;
        }

        try
        {
            // Native MSAL interactive acquisition. With nested-app auth the Office
            // host brokers this natively (no blocked browser popup).
            const result = await this.msalInstance.acquireTokenPopup(request as any);
            return result.idToken;
        }
        catch (error)
        {
            // Only fall back to the Office Dialog API if an actual browser popup is
            // blocked (non-NAA / desktop edge cases).
            if (this.isPopupBlocked(error))
            {
                return this.acquireIdTokenViaDialog(useAccountPicker, loginHint);
            }
            throw error;
        }
    }

    public async logoutMsal(): Promise<void>
    {
        this.setLoggedOutFlag(true);
        try
        {
            await this.initAccountMsal();
            await this.accountMsalInstance.clearCache();
        }
        catch (error)
        {
            logger.log(this.constructor.name, 'logoutMsal', 'account cache could not be cleared', 'log', error);
        }
        try
        {
            await this.initMsal();
            await this.msalInstance.clearCache();
        }
        catch (error)
        {
            // clearCache is unsupported in the NAA (nested app) environment: the
            // Office host owns the identity, so there is no local account to clear.
            // Expected, not a failure — our logout clears the backend session and
            // sets the logged-out flag; account switching goes through the dialog.
            logger.log(this.constructor.name, 'logoutMsal', 'clearCache unsupported (NAA)', 'log', error);
        }
    }

    public getPreferredAccount(): IPreferredMicrosoftAccount | null
    {
        const current = localStorage.getItem(OfficeBase.R2_MSAL_PREFERRED_ACCOUNT);
        const legacy = current === null ? localStorage.getItem(OfficeBase.LEGACY_PREFERRED_ACCOUNT) : null;
        const stored = current ?? legacy;
        if (!stored)
        {
            return null;
        }
        const account = JSON.parse(stored) as IPreferredMicrosoftAccount;
        if (typeof account?.tenantId === 'string' && account.tenantId
            && typeof account.subject === 'string' && account.subject
            && (account.loginHint === undefined || typeof account.loginHint === 'string'))
        {
            const preferred = { tenantId: account.tenantId, subject: account.subject, loginHint: account.loginHint };
            if (legacy !== null) this.setPreferredAccount(preferred);
            return preferred;
        }
        throw new Error('Invalid saved Microsoft account');
    }

    public setPreferredAccount(account: IPreferredMicrosoftAccount): void
    {
        localStorage.setItem(OfficeBase.R2_MSAL_PREFERRED_ACCOUNT, JSON.stringify({
            tenantId: account.tenantId, subject: account.subject, loginHint: account.loginHint,
        }));
        localStorage.removeItem(OfficeBase.LEGACY_PREFERRED_ACCOUNT);
    }

    public isLoggedOutFlagSet(): boolean
    {
        if (localStorage.getItem(OfficeBase.R2_MSAL_LOGGED_OUT) === 'true') return true;
        if (localStorage.getItem(OfficeBase.LEGACY_LOGGED_OUT) !== 'true') return false;
        this.setLoggedOutFlag(true);
        return true;
    }

    public setLoggedOutFlag(value: boolean): void
    {
        if (value)
        {
            localStorage.setItem(OfficeBase.R2_MSAL_LOGGED_OUT, 'true');
            localStorage.removeItem(OfficeBase.R2_MSAL_PREFERRED_ACCOUNT);
            localStorage.removeItem(OfficeBase.LEGACY_PREFERRED_ACCOUNT);
        }
        else
        {
            localStorage.removeItem(OfficeBase.R2_MSAL_LOGGED_OUT);
        }
        localStorage.removeItem(OfficeBase.LEGACY_LOGGED_OUT);
    }

    private isPopupBlocked(error: unknown): boolean
    {
        const code = (error as { errorCode?: string; })?.errorCode;
        return code === 'popup_window_error' || code === 'empty_window_error';
    }

    private isWebHost(): boolean
    {
        if (this.hostType === Office.HostType.Outlook)
        {
            // New Outlook can report OfficeOnline; only OutlookWebApp identifies the web host.
            return Office.context?.mailbox?.diagnostics?.hostName === 'OutlookWebApp';
        }

        return this.platformType === Office.PlatformType.OfficeOnline;
    }

    private async acquireIdTokenViaDialog(useAccountPicker: boolean, loginHint?: string | null): Promise<string>
    {
        const dialogUrl = new URL(`${ location.origin }${ environment.msal_dialog_path }`);
        dialogUrl.searchParams.set('prompt', useAccountPicker ? 'select_account' : '');
        if (!useAccountPicker && loginHint)
        {
            dialogUrl.searchParams.set('login_hint', loginHint);
        }
        return this.showAuthDialog(dialogUrl.toString());
    }

    public async showAuthDialog(startAddress: string): Promise<string>
    {
        return new Promise((resolve, reject) =>
        {
            Office.context.ui.displayDialogAsync(startAddress, { width: 50, height: 50 }, (res) =>
            {
                if (res.status !== Office.AsyncResultStatus.Succeeded)
                {
                    reject(res.error);
                    return;
                }

                const dialog = res.value;
                let settled = false;

                const settle = (fn: () => void) =>
                {
                    if (settled)
                    {
                        return;
                    }
                    settled = true;
                    try
                    {
                        dialog.close();
                    }
                    catch (e)
                    {
                        logger.error(e);
                    }
                    fn();
                };

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error Office typings
                dialog.addEventHandler(Office.EventType.DialogEventReceived, (args: { error: number; }) =>
                {
                    settle(() => reject(new Error(`Auth dialog closed (code ${ args?.error })`)));
                });

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error Office typings
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (data: { message: string; }) =>
                {
                    try
                    {
                        const payload = JSON.parse(data.message) as { idToken?: string;
                            authResult?: SerializedAuthenticationResult; error?: string; };
                        const idToken = payload.idToken;
                        if (idToken)
                        {
                            if (!payload.authResult)
                            {
                                settle(() => resolve(idToken));
                                return;
                            }
                            if (payload.authResult.idToken !== idToken)
                                throw new Error('Auth dialog returned mismatched Microsoft proof');

                            // Office dialogs can have a different localStorage partition from
                            // the task pane. Seed this runtime's ordinary MSAL cache before
                            // closing the dialog so the selected account survives reopening.
                            void this.initAccountMsal()
                                .then(() =>
                                {
                                    const result = deserializeAuthenticationResult(payload.authResult!);
                                    return this.accountMsalInstance.hydrateCache(result,
                                        { scopes: OfficeBase.MSAL_SCOPES, account: result.account });
                                })
                                .then(() => settle(() => resolve(idToken)), error => settle(() => reject(error)));
                        }
                        else
                        {
                            settle(() => reject(new Error(payload.error ?? 'Auth dialog failed')));
                        }
                    }
                    catch (e)
                    {
                        settle(() => reject(e as Error));
                    }
                });
            });
        });
    }

    public async showDialog(
        startAddress: string,
        options?: Office.DialogOptions,
        pollCondition?: () => Promise<boolean>,
    ): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            const windowOptions = {
                width: 50,
                height: 50,
                ...options,
            };

            Office.context.ui.displayDialogAsync(startAddress, windowOptions, (res) =>
            {
                if (res.status !== Office.AsyncResultStatus.Succeeded)
                {
                    logger.error('Error displaying dialog:', res.error);
                    reject(res.error);
                    return;
                }

                const dialog = res.value;
                let settled = false;
                let pollTimer: ReturnType<typeof setInterval> | null = null;

                const settle = (success: boolean, errorMsg?: string) =>
                {
                    if (settled) return;
                    settled = true;
                    if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null; }
                    try { dialog.close(); } catch (e) { logger.error(e); }
                    if (success) { resolve(); }
                    else { reject(errorMsg ?? 'Login failed'); }
                };

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                dialog.addEventHandler(Office.EventType.DialogEventReceived, (args: { error: number; }) =>
                {
                    settle(false, `Dialog closed (code ${ args?.error })`);
                });

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (data: IAuthModalParams) =>
                {
                    if (data?.error)
                    {
                        settle(false, `Message from auth modal error: ${ data.error }`);
                        return;
                    }
                    try
                    {
                        const message = JSON.parse(data.message) as unknown as IAuthModalMessage;
                        if (message.type === AUTH_MESSAGE_TYPE)
                        {
                            settle(message.isLoggedIn, message.error ?? 'Login failed');
                        }
                    }
                    catch (e) { logger.error(e); }
                });

                if (pollCondition)
                {
                    const poll = async () =>
                    {
                        if (settled) return;
                        try
                        {
                            const ok = await pollCondition();
                            if (ok) { settle(true); }
                        }
                        catch (e) { logger.error('showDialog poll error:', e); }
                    };
                    pollTimer = setInterval(poll, 3_000);
                }
            });
        });
    }

    public async setSelectedTextAsync(plainText: string): Promise<void>
    {
        const host = this.getHost();
        if (host?.checkEditable() === true)
        {
            await host.setSelectedTextAsync(plainText);
        }
    }

    public async setSelectedImageAsync(filename: string, b64Text: string): Promise<void>
    {
        const host = this.getHost();
        if (host?.checkEditable() === true)
        {
            await host.setSelectedImageAsync(filename, b64Text);
        }
    }

    abstract getHost(): IOfficeHost;

    private async updateUserProfile(): Promise<void>
    {
        try
        {
            if (this.hostType == Office.HostType.Outlook)
            {
                const userProfile = Office.context.mailbox?.userProfile;
                this.userEmail = userProfile?.emailAddress;

                return;
            }
            else
            {
                this.userEmail = await this.getLoginHint();
            }

            return;
        } catch (e)
        {
            logger.error(e);
        }
    }

    private officeItemChanged(event: Event)
    {
        logger.log(`officeItemChanged(${ JSON.stringify(event.type) })`);

        if (this.onItemChanged)
        {
            this.onItemChanged(event);
        }
    }

    private onItemChanged(event: Event): void
    {
        logger.log(event);
        logger.log('CALL ON ITEM CHANGE FN');
    }
}
